(() => {
  'use strict';

  const initialiseFinders = (scope = document) => {
    scope.querySelectorAll('[data-pev-finder]:not([data-pev-finder-ready])').forEach((finder) => {
      finder.dataset.pevFinderReady = 'true';
      const tabs = Array.from(finder.querySelectorAll('[data-pev-finder-tab]'));
      const panels = Array.from(finder.querySelectorAll('[data-pev-finder-panel]'));
      if (!tabs.length || tabs.length !== panels.length) return;

      const activate = (index, moveFocus = false) => {
        tabs.forEach((tab, tabIndex) => {
          const active = tabIndex === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel, panelIndex) => {
          const active = panelIndex === index;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
        if (moveFocus) tabs[index]?.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(index));
        tab.addEventListener('keydown', (event) => {
          let target = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = tabs.length - 1;
          if (target === null) return;
          event.preventDefault();
          activate(target, true);
        });
      });
    });
  };

  const initialiseMobileActions = (scope = document) => {
    scope.querySelectorAll('[data-pev-mobile-actions]:not([data-pev-mobile-actions-ready])').forEach((dock) => {
      dock.dataset.pevMobileActionsReady = 'true';
      const update = () => {
        const show = window.scrollY > Math.min(360, window.innerHeight * 0.45);
        dock.classList.toggle('is-visible', show);
      };
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    });
  };

  const initialiseProductBuybars = (scope = document) => {
    scope.querySelectorAll('[data-pev-product-buybar]:not([data-pev-product-buybar-ready])').forEach((bar) => {
      bar.dataset.pevProductBuybarReady = 'true';
      const sectionId = bar.dataset.sectionId;
      const productInfo = document.getElementById(`MainProduct-${sectionId}`)
        || document.querySelector(`product-info[data-section="${sectionId}"]`);
      const originalButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
      const originalButtons = originalButton?.closest('.product-form__buttons') || originalButton?.closest('product-form');
      const barButton = bar.querySelector('[data-pev-buybar-button]');
      const barLabel = bar.querySelector('[data-pev-buybar-label]');
      const barPrice = bar.querySelector('[data-pev-buybar-price]');
      const mainPrice = document.getElementById(`price-${sectionId}`);
      if (!productInfo || !barButton || !originalButton) return;

      document.body.classList.add('pev-has-product-buybar');

      const syncButton = () => {
        const disabled = originalButton.disabled
          || originalButton.hasAttribute('disabled')
          || originalButton.getAttribute('aria-disabled') === 'true';
        barButton.disabled = disabled;
        barButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        const sourceLabel = originalButton.querySelector('span')?.textContent || originalButton.textContent;
        if (barLabel && sourceLabel?.trim()) barLabel.textContent = sourceLabel.trim();
      };

      const syncPrice = () => {
        if (!barPrice || !mainPrice) return;
        const priceNode = mainPrice.querySelector('.price') || mainPrice;
        barPrice.innerHTML = priceNode.innerHTML;
      };

      let originalVisible = true;
      const updateVisibility = () => {
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        const sufficientlyScrolled = window.scrollY > 180;
        bar.classList.toggle('is-visible', mobile && sufficientlyScrolled && !originalVisible);
      };

      barButton.addEventListener('click', () => {
        syncButton();
        if (barButton.disabled) return;
        originalButton.click();
      });

      syncButton();
      syncPrice();

      const buttonObserver = new MutationObserver(() => syncButton());
      buttonObserver.observe(originalButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });

      if (mainPrice) {
        const priceObserver = new MutationObserver(() => syncPrice());
        priceObserver.observe(mainPrice, { childList: true, subtree: true, characterData: true });
      }

      if ('IntersectionObserver' in window && originalButtons) {
        const visibilityObserver = new IntersectionObserver(
          (entries) => {
            originalVisible = entries[0]?.isIntersecting ?? false;
            updateVisibility();
          },
          { threshold: 0.08 }
        );
        visibilityObserver.observe(originalButtons);
      } else {
        originalVisible = false;
      }

      window.addEventListener('scroll', updateVisibility, { passive: true });
      window.addEventListener('resize', updateVisibility, { passive: true });
      updateVisibility();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId) !== String(sectionId)) return;
          window.requestAnimationFrame(() => {
            syncButton();
            syncPrice();
          });
        });
      }
    });
  };

  const initialiseFloatingLauncherAlignment = () => {
    if (document.documentElement.dataset.pevFloatingLaunchersReady === 'true') return;
    document.documentElement.dataset.pevFloatingLaunchersReady = 'true';

    const chatSelectors = [
      '#ShopifyChat iframe',
      '#shopify-chat iframe',
      '[data-shopify-chat] iframe',
      'iframe[title*="Shopify Inbox" i]',
      'iframe[title*="chat with us" i]',
      'iframe[src*="shopifyinbox" i]',
      'iframe[src*="shopify-chat" i]'
    ];
    const smileSelectors = [
      '.smile-launcher-frame-container',
      '.smile-launcher-frame',
      '[id*="smile-launcher" i]',
      'iframe[title*="Smile" i]',
      'iframe[src*="smile.io" i]',
      'iframe[src*="smilecdn" i]'
    ];

    const fixedContainer = (element) => {
      let current = element;
      for (let depth = 0; current && current !== document.body && depth < 8; depth += 1) {
        if (current instanceof HTMLElement && window.getComputedStyle(current).position === 'fixed') return current;
        current = current.parentElement;
      }
      return element;
    };

    const visibleLauncher = (selectors) => {
      for (const selector of selectors) {
        for (const element of document.querySelectorAll(selector)) {
          const container = fixedContainer(element);
          if (!(container instanceof HTMLElement)) continue;
          const rect = container.getBoundingClientRect();
          const style = window.getComputedStyle(container);
          const launcherSized = rect.width >= 24 && rect.height >= 24 && rect.width <= 240 && rect.height <= 240;
          if (launcherSized && style.display !== 'none' && style.visibility !== 'hidden' && rect.bottom > 0) return container;
        }
      }
      return null;
    };

    let animationFrame = 0;
    const align = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const smileLauncher = visibleLauncher(smileSelectors);
        if (!smileLauncher) return;

        const chatLauncher = visibleLauncher(chatSelectors);
        let bottom = 16;
        if (chatLauncher) {
          const rect = chatLauncher.getBoundingClientRect();
          const measuredBottom = Math.round(window.innerHeight - rect.bottom);
          if (Number.isFinite(measuredBottom) && measuredBottom >= 0 && measuredBottom <= 180) bottom = measuredBottom;
        }

        const value = `${bottom}px`;
        document.documentElement.style.setProperty('--pev-floating-launcher-bottom', value);
        smileLauncher.style.setProperty('bottom', value, 'important');
      });
    };

    const observer = new MutationObserver(align);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', align, { passive: true });
    window.addEventListener('load', align, { once: true });
    [0, 500, 1200, 2500, 5000, 9000].forEach((delay) => window.setTimeout(align, delay));
  };

  const initialise = (scope = document) => {
    initialiseFinders(scope);
    initialiseMobileActions(scope);
    initialiseProductBuybars(scope);
    initialiseFloatingLauncherAlignment();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();

// Powered Up PEV build 7.2.0 cache-fixed
